import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import {
  ChevronLeft, Flame, Trophy, Skull, Lock, Loader2, Crown,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Mock current-season state. Will be replaced by reads of:
//   - Anchor `Season` PDA (current season number, milestone target, total burned, end_ts)
//   - Anchor `RewardPool` PDA (carryover amount)
//   - Top SP holders sorted from PlayerAccount PDAs
// Program ID: C5vRjZZAjG1uzcir9y2tw6dwMjH79fPwQgoKaSjEnACT
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_SEASON_STATE = {
  seasonNumber: 3,
  milestoneTarget: 1_000_000_000,   // 1B $GFG
  totalBurned: 1_120_400_000,        // milestone struck (>target)
  startedAt: '2026-04-01',
  endsAt: '2026-04-30',
  carryoverFromLast: 0,
  rewardPoolGfg: 250_000_000,
  topHolders: [
    { rank: 1, wallet: '7xBQjf3vJ9Ri8XkSAdmZWJ8KKoBrYpjFeLcRkLW2NhJN', sp: 184_320, share: 35 },
    { rank: 2, wallet: 'C2kQkiowaVKZDXeFd7LE84WZJowrpgYA7DrhPpxqY6S4', sp: 168_905, share: 25 },
    { rank: 3, wallet: 'D9pNvQ8mYfEkRdLs3VhT6oPjQ2cGzBH4FmKn7AaR1tWv', sp: 142_780, share: 18 },
    { rank: 4, wallet: '4tHjK9LpQrTzM2vN8XwYf7Jb3CcDsEgF6AaGgB1UuV5N', sp: 121_004, share: 12 },
    { rank: 5, wallet: 'EpZN3qXvL8m2KdH9YwR4PsTjC5nB7AaG6FfDdEeS1uV2', sp: 108_550, share: 10 },
  ],
};

const truncate = (s) => `${s.slice(0, 5)}…${s.slice(-5)}`;

const ProgressBar = ({ pct, struck }) => (
  <div className="relative h-3 w-full overflow-hidden" style={{
    background: 'rgba(8,4,2,0.95)',
    border: '1px solid rgba(140,100,40,0.4)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.7)',
    borderRadius: 1,
  }}>
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${Math.min(pct, 100)}%` }}
      transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
      className="h-full"
      style={{
        background: struck
          ? 'linear-gradient(90deg, rgba(255,140,0,0.95) 0%, rgba(255,200,80,1) 50%, rgba(255,140,0,0.95) 100%)'
          : 'linear-gradient(90deg, rgba(160,80,30,0.9) 0%, rgba(220,140,60,0.95) 100%)',
        boxShadow: struck
          ? '0 0 14px rgba(255,180,80,0.85), inset 0 1px 1px rgba(255,220,140,0.6)'
          : '0 0 8px rgba(220,140,60,0.5)',
      }}
    />
    {/* Milestone mark at 100% */}
    <div className="absolute top-0 bottom-0" style={{
      left: '100%', transform: 'translateX(-1px)',
      width: 2, background: '#ffd86b',
      boxShadow: '0 0 8px #ffd86b',
    }} />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// CEREMONIES
// ─────────────────────────────────────────────────────────────────────────────
const StruckCeremony = ({ state, onContinue }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    className="relative px-6 py-10 text-center"
    data-testid="ceremony-struck"
    style={{
      background: 'linear-gradient(180deg, rgba(28,18,6,0.99) 0%, rgba(12,7,2,1) 100%)',
      border: '1px solid rgba(255,180,80,0.7)',
      boxShadow: `
        inset 0 1px 1px rgba(255,210,120,0.35),
        inset 0 -1px 1px rgba(0,0,0,0.8),
        0 0 0 2px rgba(8,4,2,1),
        0 0 0 3px rgba(220,170,80,0.65),
        0 0 60px rgba(255,160,40,0.45),
        0 8px 22px rgba(0,0,0,0.8)
      `,
    }}
  >
    {/* Confetti embers */}
    {[...Array(40)].map((_, i) => {
      const angle = (i / 40) * Math.PI * 2;
      const dist = 220 + (i * 11) % 180;
      return (
        <div key={i} className="absolute pointer-events-none" style={{
          top: '50%', left: '50%',
          width: 4, height: 4, borderRadius: '50%',
          background: i % 4 === 0 ? '#ffd700' : '#ff8c00',
          boxShadow: `0 0 6px ${i % 4 === 0 ? '#ffd700' : '#ff8c00'}`,
          '--dx': `${Math.cos(angle) * dist}px`,
          '--dy': `${Math.sin(angle) * dist}px`,
          animation: `emberBurst ${1 + (i * 13) % 8 / 10}s ${(i * 7) % 12 / 30}s cubic-bezier(0.22, 1, 0.36, 1) forwards`,
        }} />
      );
    })}

    <Crown className="w-16 h-16 mx-auto mb-4" style={{
      color: '#ffd86b',
      filter: 'drop-shadow(0 0 16px #ffd86b) drop-shadow(0 0 32px rgba(255,140,0,0.5))',
    }} />
    <p className="text-[10px] tracking-[0.4em] mb-3" style={{
      color: '#ffd86b', fontFamily: "'Courier New', monospace",
      textShadow: '0 0 8px rgba(255,180,80,0.6)',
    }}>◆ THE PYRE BURNED TRUE ◆</p>
    <h2 className="text-4xl sm:text-5xl font-black mb-3" style={{
      fontFamily: "'Cinzel', serif",
      background: 'linear-gradient(180deg, #fff5dc 0%, #f0d68a 50%, #c69544 100%)',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
      filter: 'drop-shadow(0 0 24px rgba(255,180,80,0.6))',
    }}>MILESTONE STRUCK</h2>
    <p className="text-base mb-6 max-w-md mx-auto leading-relaxed" style={{
      color: '#bba377', fontFamily: "'Cinzel', serif",
    }}>
      The community offered <span style={{ color: '#ffd86b', fontWeight: 700 }}>{(state.totalBurned / 1_000_000).toFixed(1)}M $GFG</span> to the flame —
      surpassing the {(state.milestoneTarget / 1_000_000).toFixed(0)}M milestone.
      The reward pool is unlocked. Top souls will receive their share.
    </p>

    <div className="max-w-md mx-auto mb-6 px-4 py-3" style={{
      background: 'rgba(8,4,2,0.7)',
      border: '1px solid rgba(220,170,80,0.4)',
    }}>
      <p className="text-[9px] tracking-[0.3em] mb-1" style={{ color: '#ffc870', fontFamily: "'Courier New', monospace" }}>POOL DISTRIBUTED</p>
      <p className="text-3xl font-black" style={{
        fontFamily: "'Impact', sans-serif",
        color: '#fff5dc',
        textShadow: '0 0 12px rgba(255,180,80,1), 0 0 22px rgba(255,140,0,0.7), 0 1px 2px rgba(0,0,0,1)',
      }}>{(state.rewardPoolGfg / 1_000_000).toFixed(0)}M $GFG</p>
    </div>

    <p className="text-[10px] mb-4" style={{ color: '#ffc870', fontFamily: "'Cinzel', serif", letterSpacing: '0.2em' }}>
      ◆ TOP 4 IMMORTALISED · S{state.seasonNumber} ◆
    </p>
    <div className="max-w-md mx-auto space-y-1.5 mb-6" data-testid="ceremony-rewards-table">
      {state.topHolders.slice(0, 4).map((h) => (
        <div key={h.rank} className="flex items-center justify-between px-3 py-2 text-[12px]" style={{
          background: h.rank === 1 ? 'rgba(255,180,60,0.12)' : 'rgba(8,4,2,0.7)',
          border: `1px solid ${h.rank === 1 ? 'rgba(255,180,80,0.5)' : 'rgba(140,100,40,0.3)'}`,
        }}>
          <div className="flex items-center gap-2">
            <span className="font-bold tabular-nums" style={{ color: h.rank === 1 ? '#ffd86b' : '#a08858', fontFamily: "'Cinzel', serif" }}>#{h.rank}</span>
            <span className="font-mono" style={{ color: '#bba377' }}>{truncate(h.wallet)}</span>
          </div>
          <span className="font-bold" style={{ color: '#ffd86b', fontFamily: "'Impact', sans-serif" }}>
            {((state.rewardPoolGfg * h.share) / 100 / 1_000_000).toFixed(1)}M
          </span>
        </div>
      ))}
    </div>

    <button
      onClick={onContinue}
      data-testid="ceremony-continue"
      className="px-6 py-3 text-[11px] font-bold tracking-[0.25em]"
      style={{
        fontFamily: "'Cinzel', serif",
        background: 'linear-gradient(180deg, rgba(40,24,8,0.98) 0%, rgba(20,12,4,1) 100%)',
        border: '1px solid rgba(220,170,80,0.85)',
        color: '#ffd86b',
        textShadow: '0 0 8px rgba(255,180,80,0.6)',
      }}
    >
      OPEN SEASON {state.seasonNumber + 1}
    </button>
  </motion.div>
);

const ColdCeremony = ({ state, onContinue }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.7 }}
    className="relative px-6 py-10 text-center"
    data-testid="ceremony-cold"
    style={{
      background: 'linear-gradient(180deg, rgba(20,10,12,0.99) 0%, rgba(8,4,4,1) 100%)',
      border: '1px solid rgba(180,60,60,0.5)',
      boxShadow: `
        inset 0 1px 1px rgba(120,40,40,0.25),
        inset 0 -1px 1px rgba(0,0,0,0.8),
        0 0 0 2px rgba(8,4,2,1),
        0 0 0 3px rgba(120,40,40,0.5),
        0 0 40px rgba(120,40,40,0.4),
        0 8px 22px rgba(0,0,0,0.8)
      `,
    }}
  >
    <Skull className="w-16 h-16 mx-auto mb-4" style={{
      color: '#a85858',
      filter: 'drop-shadow(0 0 16px rgba(180,60,60,0.7)) drop-shadow(0 0 32px rgba(120,40,40,0.4))',
    }} />
    <p className="text-[10px] tracking-[0.4em] mb-3" style={{
      color: '#ff7878', fontFamily: "'Courier New', monospace",
      textShadow: '0 0 8px rgba(180,60,60,0.6)',
    }}>◆ THE PYRE BURNED COLD ◆</p>
    <h2 className="text-4xl sm:text-5xl font-black mb-3" style={{
      fontFamily: "'Cinzel', serif",
      background: 'linear-gradient(180deg, #ffd0c0 0%, #c69090 50%, #6e3030 100%)',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    }}>MILESTONE UNCLAIMED</h2>
    <p className="text-base mb-6 max-w-md mx-auto leading-relaxed" style={{
      color: '#bba377', fontFamily: "'Cinzel', serif",
    }}>
      The flame received <span style={{ color: '#ff8b6b', fontWeight: 700 }}>{(state.totalBurned / 1_000_000).toFixed(1)}M</span> — short of the {(state.milestoneTarget / 1_000_000).toFixed(0)}M offering.
      The reward pool is sealed and carried into the next season, where the price of glory rises.
    </p>

    <div className="max-w-md mx-auto mb-6 px-4 py-3" style={{
      background: 'rgba(8,4,2,0.7)',
      border: '1px solid rgba(180,60,60,0.4)',
    }}>
      <p className="text-[9px] tracking-[0.3em] mb-1" style={{ color: '#ff8b6b', fontFamily: "'Courier New', monospace" }}>CARRIED INTO SEASON {state.seasonNumber + 1}</p>
      <p className="text-3xl font-black" style={{
        fontFamily: "'Impact', sans-serif",
        color: '#fff5dc',
        textShadow: '0 0 12px rgba(220,90,70,0.85), 0 1px 2px rgba(0,0,0,1)',
      }}>{(state.rewardPoolGfg / 1_000_000).toFixed(0)}M $GFG</p>
    </div>

    <button
      onClick={onContinue}
      data-testid="ceremony-continue"
      className="px-6 py-3 text-[11px] font-bold tracking-[0.25em]"
      style={{
        fontFamily: "'Cinzel', serif",
        background: 'linear-gradient(180deg, rgba(28,8,8,0.98) 0%, rgba(14,4,4,1) 100%)',
        border: '1px solid rgba(220,90,70,0.7)',
        color: '#ff8b6b',
        textShadow: '0 0 8px rgba(220,90,70,0.6)',
      }}
    >
      OPEN SEASON {state.seasonNumber + 1}
    </button>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN
// ─────────────────────────────────────────────────────────────────────────────
export default function SeasonSettlementPage() {
  const { publicKey, connected } = useWallet();
  const walletAddress = publicKey?.toBase58();
  const [authStatus, setAuthStatus] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [state, setState] = useState(MOCK_SEASON_STATE);
  // 'idle' | 'settling' | 'struck' | 'cold'
  const [phase, setPhase] = useState('idle');
  const [outcomeSimulator, setOutcomeSimulator] = useState('actual'); // 'actual' | 'force-struck' | 'force-cold'

  // Real admin gate via existing backend endpoint
  useEffect(() => {
    if (!walletAddress) { setAuthStatus(null); setAuthLoading(false); return; }
    fetch(`${API_URL}/api/admin/auth/check?wallet_address=${walletAddress}`)
      .then((r) => r.json())
      .then((d) => { setAuthStatus(d); setAuthLoading(false); })
      .catch(() => setAuthLoading(false));
  }, [walletAddress]);

  const isAdmin = authStatus?.is_admin === true;

  const milestonePct = (state.totalBurned / state.milestoneTarget) * 100;
  const milestoneStruck = state.totalBurned >= state.milestoneTarget;

  const handleSettle = () => {
    setPhase('settling');
    // Mock on-chain delay. Real impl: call settle_season instruction on Anchor program.
    setTimeout(() => {
      let outcome;
      if (outcomeSimulator === 'force-struck') outcome = 'struck';
      else if (outcomeSimulator === 'force-cold') outcome = 'cold';
      else outcome = milestoneStruck ? 'struck' : 'cold';
      setPhase(outcome);
    }, 2400);
  };

  const handleContinue = () => {
    // Mock: roll season + reset
    setState((s) => ({
      ...s,
      seasonNumber: s.seasonNumber + 1,
      totalBurned: 0,
      carryoverFromLast: phase === 'cold' ? s.rewardPoolGfg : 0,
      startedAt: new Date().toISOString().slice(0, 10),
      endsAt: new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10),
    }));
    setPhase('idle');
  };

  return (
    <div className="min-h-screen text-white relative" style={{ background: '#020100' }}>
      <style>{`
        @keyframes emberBurst {
          0%   { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) scale(0); opacity: 0; }
        }
        @keyframes settlePulse {
          0%, 100% { box-shadow: 0 0 28px rgba(255,140,0,0.4), inset 0 0 14px rgba(255,140,0,0.1); }
          50% { box-shadow: 0 0 44px rgba(255,180,80,0.7), inset 0 0 22px rgba(255,180,80,0.2); }
        }
      `}</style>

      {/* Atmospheric backdrop */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `
          radial-gradient(ellipse at 50% 0%, rgba(255,69,0,0.10) 0%, transparent 55%),
          radial-gradient(ellipse at 50% 100%, rgba(120,40,30,0.06) 0%, transparent 60%)
        `,
      }} />

      <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-8 py-10 pb-32">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] mb-6 group" style={{
          color: '#a08858', fontFamily: "'Courier New', monospace",
        }} data-testid="settle-back">
          <ChevronLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
          ADMIN COURT
        </Link>

        <div className="text-center mb-10">
          <p className="text-[10px] tracking-[0.4em] mb-3" style={{
            color: '#9a7a3a', fontFamily: "'Courier New', monospace",
          }}>◆ ADMIN · SEASON CLOSING CEREMONY ◆</p>
          <h1 className="text-4xl sm:text-5xl font-black mb-3" style={{
            fontFamily: "'Cinzel', serif",
            background: 'linear-gradient(180deg, #f0d68a 0%, #c69544 50%, #6e4814 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            textShadow: '0 0 32px rgba(255,180,80,0.4)',
          }}>SEAL THE SEASON</h1>
          <p className="text-sm" style={{ color: '#bba377', fontFamily: "'Cinzel', serif" }}>
            Settle Season {state.seasonNumber}. Distribute the pyre's reward — or carry it forward.
          </p>
        </div>

        {/* Auth gate */}
        {authLoading ? (
          <div className="text-center py-20"><Loader2 className="w-6 h-6 mx-auto animate-spin" style={{ color: '#ffc870' }} /></div>
        ) : !connected ? (
          <div className="text-center py-12 px-5" data-testid="settle-auth-required" style={{
            background: 'rgba(8,4,2,0.85)',
            border: '1px solid rgba(140,100,40,0.4)',
          }}>
            <Lock className="w-10 h-10 mx-auto mb-3" style={{ color: '#a08858' }} />
            <p className="text-sm mb-4" style={{ color: '#bba377', fontFamily: "'Cinzel', serif" }}>
              Connect the admin wallet to seal the season.
            </p>
            <WalletMultiButton />
          </div>
        ) : !isAdmin ? (
          <div className="text-center py-12 px-5" data-testid="settle-not-admin" style={{
            background: 'rgba(20,4,4,0.85)',
            border: '1px solid rgba(180,60,60,0.4)',
          }}>
            <Skull className="w-10 h-10 mx-auto mb-3" style={{ color: '#a85858' }} />
            <p className="text-sm" style={{ color: '#bba377', fontFamily: "'Cinzel', serif" }}>
              This wallet is not the chamberlain. Only the Sovereign's admin may seal the season.
            </p>
            <p className="text-[10px] font-mono mt-3" style={{ color: '#7a6648' }}>{walletAddress}</p>
          </div>
        ) : (
          <>
            <AnimatePresence mode="wait">
              {phase === 'idle' && (
                <motion.div
                  key="idle"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  className="space-y-6"
                  data-testid="settle-idle-panel"
                >
                  {/* Season summary panel */}
                  <div className="px-6 py-6" style={{
                    background: 'linear-gradient(180deg, rgba(28,18,6,0.99) 0%, rgba(12,7,2,1) 100%)',
                    border: '1px solid rgba(220,170,80,0.55)',
                    boxShadow: 'inset 0 1px 1px rgba(220,170,80,0.25), inset 0 -1px 1px rgba(0,0,0,0.7), 0 6px 22px rgba(0,0,0,0.7)',
                  }}>
                    <div className="flex items-baseline justify-between mb-4">
                      <p className="text-[10px] tracking-[0.3em] font-bold" style={{ color: '#ffc870', fontFamily: "'Courier New', monospace" }}>
                        SEASON {state.seasonNumber} · DAY 30
                      </p>
                      <p className="text-[10px] font-mono" style={{ color: '#7a6648' }}>{state.startedAt} → {state.endsAt}</p>
                    </div>

                    <p className="text-[9px] tracking-[0.25em] mb-2" style={{ color: '#a08858', fontFamily: "'Courier New', monospace" }}>
                      MILESTONE PROGRESS
                    </p>
                    <ProgressBar pct={milestonePct} struck={milestoneStruck} />
                    <div className="flex items-baseline justify-between mt-2">
                      <p className="text-2xl font-black" style={{
                        fontFamily: "'Impact', sans-serif",
                        color: milestoneStruck ? '#ffd86b' : '#ff8b6b',
                        textShadow: milestoneStruck
                          ? '0 0 12px rgba(255,180,80,0.85), 0 1px 2px rgba(0,0,0,1)'
                          : '0 0 12px rgba(220,90,70,0.7), 0 1px 2px rgba(0,0,0,1)',
                      }} data-testid="settle-burned-amount">
                        {(state.totalBurned / 1_000_000).toFixed(1)}M
                      </p>
                      <p className="text-[11px] font-mono" style={{ color: '#a08858' }}>
                        / {(state.milestoneTarget / 1_000_000).toFixed(0)}M target
                      </p>
                    </div>
                    <p className="text-[10px] mt-2 tracking-[0.15em] font-bold" style={{
                      color: milestoneStruck ? '#88e878' : '#ff7878',
                      fontFamily: "'Cinzel', serif",
                      textShadow: `0 0 6px ${milestoneStruck ? 'rgba(120,220,120,0.5)' : 'rgba(220,90,70,0.5)'}`,
                    }}>
                      {milestoneStruck ? '◆ MILESTONE STRUCK — POOL UNLOCKS ON SETTLE' : '◇ MILESTONE UNMET — POOL CARRIES FORWARD'}
                    </p>
                  </div>

                  {/* Ceremony outcome simulator (admin tool, mocked) */}
                  <div className="px-5 py-4" style={{
                    background: 'rgba(8,4,2,0.85)',
                    border: '1px dashed rgba(140,100,40,0.4)',
                  }}>
                    <p className="text-[10px] tracking-[0.25em] mb-2 font-bold" style={{
                      color: '#ffc870', fontFamily: "'Cinzel', serif",
                    }}>OUTCOME SIMULATOR (MOCK)</p>
                    <p className="text-[10px] mb-3" style={{ color: '#7a6648' }}>
                      For the hackathon demo: force a ceremony outcome regardless of burn total.
                      Will be removed once the Anchor program is live.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { id: 'actual', label: 'AUTO (USE BURN TOTAL)' },
                        { id: 'force-struck', label: 'FORCE: PYRE TRUE' },
                        { id: 'force-cold', label: 'FORCE: PYRE COLD' },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => setOutcomeSimulator(opt.id)}
                          data-testid={`sim-${opt.id}`}
                          className="px-3 py-1.5 text-[9px] font-bold tracking-[0.18em] transition-all"
                          style={{
                            fontFamily: "'Cinzel', serif",
                            background: outcomeSimulator === opt.id
                              ? 'linear-gradient(180deg, rgba(40,24,8,0.98) 0%, rgba(20,12,4,1) 100%)'
                              : 'rgba(8,4,2,0.7)',
                            border: `1px solid ${outcomeSimulator === opt.id ? 'rgba(220,170,80,0.7)' : 'rgba(140,100,40,0.3)'}`,
                            color: outcomeSimulator === opt.id ? '#ffd86b' : '#a08858',
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* THE BIG SETTLE BUTTON */}
                  <motion.button
                    onClick={handleSettle}
                    data-testid="settle-the-season-button"
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className="w-full px-8 py-5 text-[14px] font-black tracking-[0.35em] relative"
                    style={{
                      fontFamily: "'Cinzel', serif",
                      background: 'linear-gradient(180deg, rgba(40,24,8,0.99) 0%, rgba(20,12,4,1) 100%)',
                      border: '1.5px solid rgba(220,170,80,0.85)',
                      color: '#ffd86b',
                      textShadow: '0 0 12px rgba(255,180,80,0.85), 0 1px 2px rgba(0,0,0,1)',
                      animation: 'settlePulse 3.2s ease-in-out infinite',
                    }}
                  >
                    <Flame className="w-5 h-5 inline-block mr-2 -mt-1" style={{ filter: 'drop-shadow(0 0 6px #ffd86b)' }} />
                    SEAL SEASON {state.seasonNumber}
                  </motion.button>

                  <p className="text-[10px] text-center" style={{ color: '#5a4828', fontFamily: "'Courier New', monospace", letterSpacing: '0.2em' }}>
                    ◆ SETTLES VIA settle_season() · PROGRAM C5vR…nACT (MOCKED) ◆
                  </p>
                </motion.div>
              )}

              {phase === 'settling' && (
                <motion.div
                  key="settling"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center py-20"
                  data-testid="settle-loading"
                >
                  <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: '#ffd86b' }} />
                  <p className="text-[12px] tracking-[0.3em] mb-2" style={{ color: '#ffd86b', fontFamily: "'Cinzel', serif" }}>
                    THE SOVEREIGN COUNTS THE OFFERING
                  </p>
                  <p className="text-[10px]" style={{ color: '#7a6648' }}>broadcasting settle_season transaction…</p>
                </motion.div>
              )}

              {phase === 'struck' && <StruckCeremony state={state} onContinue={handleContinue} />}
              {phase === 'cold' && <ColdCeremony state={state} onContinue={handleContinue} />}
            </AnimatePresence>
          </>
        )}
      </div>
    </div>
  );
}
